import {
  Ban,
  Building2,
  CheckCircle2,
  Clock,
  FileSignature,
  Handshake,
  KeyRound,
} from "lucide-react";
import { useGetMany, useGetOne, useTranslate } from "ra-core";
import { createContext, useContext, useMemo, type ComponentType } from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { ResponsableWithContact } from "../team/ResponsableWithContact";
import { TeamContactsProvider } from "../team/TeamContactsContext";
import type {
  InventoryItem,
  InventoryOrigin,
  PropertyFurnished,
  PropertyOperation,
  PropertyOption,
  PropertyOptionStatus,
  Sale,
} from "../types";

const STATUS_STYLES: Record<
  PropertyOptionStatus,
  { Icon: ComponentType<{ className?: string }>; className: string }
> = {
  disponible: { Icon: CheckCircle2, className: "bg-[#E7F5EC] text-[#23794A]" },
  apartada: { Icon: Clock, className: "bg-[#FDF6D3] text-[#7A6500]" },
  vendida: { Icon: Handshake, className: "bg-[#E6ECF5] text-[#1F3A68]" },
  retirada: { Icon: Ban, className: "bg-[#FBE7E4] text-[#B3321C]" },
};

/** Status of an option: always an icon AND text, never colour alone. */
export const OpcionStatusPill = ({
  status,
}: {
  status: PropertyOptionStatus;
}) => {
  const translate = useTranslate();
  const style = STATUS_STYLES[status];
  // A BPO unit can carry a status the options do not have: show it as plain text.
  if (!style) return <span>{status}</span>;
  const { Icon, className } = style;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium",
        className,
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
      {translate(`crm.opciones.status.${status}`)}
    </span>
  );
};

const ORIGIN_STYLES: Record<
  InventoryOrigin,
  { Icon: ComponentType<{ className?: string }>; className: string }
> = {
  opcion: { Icon: KeyRound, className: "bg-[#E6ECF5] text-[#1F3A68]" },
  exclusiva: { Icon: FileSignature, className: "bg-[#FDF6D3] text-[#7A6500]" },
  bpo: { Icon: Building2, className: "bg-[#E7F5EC] text-[#23794A]" },
};

/** Origin of an inventory row (Opción, Exclusiva, BPO): always an icon AND text. */
export const OriginPill = ({ origin }: { origin: InventoryOrigin }) => {
  const translate = useTranslate();
  const style = ORIGIN_STYLES[origin];
  if (!style) return null;
  const { Icon, className } = style;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium",
        className,
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
      {translate(`crm.opciones.origin.${origin}`)}
    </span>
  );
};

/** "Venta", or "Renta" followed by "amueblado" / "sin amueblar" for a rental. */
export const OperationLabel = ({
  operation,
  furnished,
}: {
  operation: PropertyOperation;
  furnished: PropertyFurnished | null;
}) => {
  const translate = useTranslate();
  return (
    <span>
      {translate(`crm.opciones.operation.${operation}`)}
      {operation === "renta" && furnished ? (
        <span className="block text-sm text-muted-foreground">
          {translate(`crm.opciones.furnished.${furnished}`)}
        </span>
      ) : null}
    </span>
  );
};

type KeySpecsRow = Pick<
  InventoryItem,
  "levels" | "bedrooms" | "bathrooms" | "area_m2" | "parking"
>;

/** Compact key specifications ("3 rec. · 2 baños · 120 m²"); "—" when none is set. */
export const KeySpecs = ({ item }: { item: KeySpecsRow }) => {
  const translate = useTranslate();
  const parts = (
    [
      ["levels", item.levels],
      ["bedrooms", item.bedrooms],
      ["bathrooms", item.bathrooms],
      ["area_m2", item.area_m2],
      ["parking", item.parking],
    ] as const
  )
    .filter(([, value]) => value != null)
    .map(([key, value]) => translate(`crm.opciones.specs.${key}`, { value }));
  return (
    <span className="tabular-nums">
      {parts.length > 0 ? parts.join(" · ") : "—"}
    </span>
  );
};

type ResponsableOption = Pick<
  PropertyOption,
  "responsable_sales_id" | "responsable_is_amk"
>;

/** Sales loaded once for a whole list page, keyed by id. */
const ResponsableSalesContext = createContext<Map<string, Sale> | null>(null);

/**
 * Loads the responsables of the visible options with ONE `getMany` request and
 * shares them with every `ResponsableLabel` below, instead of one request per row.
 */
export const ResponsableSalesProvider = ({
  options,
  children,
}: {
  options: ResponsableOption[];
  children: ReactNode;
}) => {
  const ids = useMemo(
    () =>
      Array.from(
        new Set(
          options
            .filter(
              (option) =>
                !option.responsable_is_amk &&
                option.responsable_sales_id != null,
            )
            .map((option) => option.responsable_sales_id as Sale["id"]),
        ),
      ),
    [options],
  );
  const { data } = useGetMany<Sale>(
    "sales",
    { ids },
    { enabled: ids.length > 0 },
  );
  const byId = useMemo(
    () => new Map((data ?? []).map((sale) => [String(sale.id), sale])),
    [data],
  );
  return (
    <ResponsableSalesContext.Provider value={byId}>
      <TeamContactsProvider salesIds={ids}>{children}</TeamContactsProvider>
    </ResponsableSalesContext.Provider>
  );
};

/**
 * The responsable of an option: the advisor's clave, or "AMK" for the agency.
 * Inside a `ResponsableSalesProvider` the advisor comes from the batched load;
 * on its own (detail page) it fetches the single advisor.
 */
export const ResponsableLabel = ({ option }: { option: ResponsableOption }) => {
  const batched = useContext(ResponsableSalesContext);
  const { data: fetched } = useGetOne<Sale>(
    "sales",
    { id: option.responsable_sales_id as Sale["id"] },
    {
      enabled:
        batched == null &&
        !option.responsable_is_amk &&
        option.responsable_sales_id != null,
    },
  );
  if (option.responsable_is_amk) return <span>AMK</span>;
  const sale =
    batched == null
      ? fetched
      : option.responsable_sales_id == null
        ? undefined
        : batched.get(String(option.responsable_sales_id));
  const label =
    sale?.advisor_code?.trim() ||
    (sale ? `${sale.first_name} ${sale.last_name}`.trim() : "—");
  if (!sale) return <span>{label}</span>;
  return (
    <ResponsableWithContact salesId={sale.id} name={label} email={sale.email} />
  );
};
